import type { FC, ReactNode } from 'react'
import type { Job, JobStatus } from '@/types'

import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { JOB_FLOW, JOB_STATUS_META } from '@/constants/carwash'
import { formatTime } from '@/lib/carwash/format'

const SHORT_LABELS: Record<JobStatus, string> = {
    waiting: 'Waiting',
    assigned: 'Assigned',
    washing: 'Washing',
    quality_check: 'Check',
    completed: 'Done',
    cancelled: 'Cancelled'
}

export const getStepTime = (job: Job, step: JobStatus): string | null => {
    switch (step) {
        case 'waiting': return job.enteredAt
        case 'assigned': return job.assignedAt
        case 'washing': return job.startedAt
        case 'quality_check': return job.qualityCheckAt
        case 'completed': return job.completedAt
        default: return job.cancelledAt
    }
}

type StepState = 'done' | 'current' | 'skipped' | 'todo'

const stepStates = (job: Job): StepState[] => {
    const currentIndex = JOB_FLOW.indexOf(job.status)

    return JOB_FLOW.map((step, index) => {
        if (job.status === 'cancelled') return getStepTime(job, step) ? 'done' : 'todo'
        if (index === currentIndex) return job.status === 'completed' ? 'done' : 'current'
        if (index < currentIndex) return getStepTime(job, step) ? 'done' : 'skipped'
        return 'todo'
    })
}

export const StatusStepper: FC<{ job: Job, orientation?: 'horizontal' | 'vertical', className?: string }> = ({
    job,
    orientation = 'horizontal',
    className
}): ReactNode => {
    const states = stepStates(job)

    if (orientation === 'vertical') {
        return (
            <ol className={cn('relative', className)}>
                {JOB_FLOW.map((step, index) => {
                    const state = states[index]
                    const time = getStepTime(job, step)
                    const last = index === JOB_FLOW.length - 1

                    return (
                        <li key={step} className='relative flex gap-3 pb-4 last:pb-0'>
                            {!last && (
                                <span
                                    className={cn('absolute top-7 bottom-0 left-[13px] w-0.5', state === 'done' ? 'bg-primary/60' : 'bg-slate-200')}
                                    aria-hidden='true'
                                />
                            )}

                            <StepDot state={state} />

                            <div className='flex min-w-0 flex-1 items-baseline justify-between gap-3 pt-0.5'>
                                <span className={cn('text-sm font-semibold', state === 'todo' || state === 'skipped' ? 'text-slate-400' : 'text-foreground')}>
                                    {JOB_STATUS_META[step].label}
                                    {state === 'current' && <span className='sr-only'> (current)</span>}
                                </span>

                                <span className='text-xs font-medium text-muted-foreground tabular-nums'>
                                    {state === 'skipped' ? 'Skipped' : time && state !== 'todo' ? formatTime(time) : ''}
                                </span>
                            </div>
                        </li>
                    )
                })}
            </ol>
        )
    }

    return (
        <ol className={cn('grid grid-cols-5', className)}>
            {JOB_FLOW.map((step, index) => {
                const state = states[index]
                const time = getStepTime(job, step)

                return (
                    <li key={step} className='relative flex flex-col items-center text-center'>
                        {index > 0 && (
                            <span
                                className={cn('absolute top-[13px] right-1/2 left-[-50%] h-0.5', state === 'done' || state === 'current' ? 'bg-primary/60' : 'bg-slate-200')}
                                aria-hidden='true'
                            />
                        )}

                        <StepDot state={state} />

                        <span className={cn('mt-1.5 text-[11px] leading-tight font-semibold', state === 'todo' || state === 'skipped' ? 'text-slate-400' : 'text-foreground')}>
                            {SHORT_LABELS[step]}
                            {state === 'current' && <span className='sr-only'> (current)</span>}
                        </span>

                        <span className='text-[10px] leading-tight font-medium text-muted-foreground tabular-nums'>
                            {state === 'skipped' ? 'Skipped' : time && state !== 'todo' ? formatTime(time) : ' '}
                        </span>
                    </li>
                )
            })}
        </ol>
    )
}

const StepDot: FC<{ state: StepState }> = ({ state }): ReactNode => {
    return (
        <span
            className={cn(
                'relative z-10 grid size-7 shrink-0 place-items-center rounded-full border-2 bg-white',
                state === 'done' && 'border-primary bg-primary text-white',
                state === 'current' && 'border-primary text-primary',
                state === 'skipped' && 'border-dashed border-slate-300 text-slate-300',
                state === 'todo' && 'border-slate-200'
            )}
            aria-hidden='true'
        >
            {state === 'done' && <Check className='size-3.5' strokeWidth={3} />}
            {state === 'current' && <span className='size-2.5 rounded-full bg-primary animate-live' />}
        </span>
    )
}
